/**
 * FlowAI Sicherheits-Schalter (#1280) — globale, Admin-only Schutzmechanismen
 * für den LIVE-Scharfschalt-Test des Vorschlags-Modus (P1c).
 *
 *   1. Dry-Run            — AN sperrt JEDE Ausführung (auch approved). Default AN.
 *   2. 2.-Bestätigung     — AN verlangt einen „Wirklich senden?"-Dialog vor jeder
 *                           echten Ausführung. Default AN.
 *
 * Werte liegen in system_config (flowai_dry_run, flowai_require_double_confirm).
 * Jeder Wechsel wird in flowai_safety_switch_log protokolliert (wer/wann).
 *
 * @page /flowai/admin/safety
 * @since v2.613.0 (#1280)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

export default function FlowAISafety() {
  const navigate = useNavigate();
  const [sw, setSw] = useState({ dry_run: true, require_double_confirm: true });
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [rSw, rLog] = await Promise.all([
        fetch(`${API}/api/flowai/safety-switches`, { credentials: 'include' }),
        fetch(`${API}/api/flowai/safety-switches/log?limit=20`, { credentials: 'include' }),
      ]);
      const dSw = await rSw.json().catch(() => ({}));
      if (dSw?.ok) setSw({ dry_run: dSw.dry_run !== false, require_double_confirm: dSw.require_double_confirm !== false });
      const dLog = await rLog.json().catch(() => ({}));
      if (dLog?.ok) setLog(Array.isArray(dLog.entries) ? dLog.entries : []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggle(which, value) {
    setSaving(which); setMsg(null);
    try {
      const r = await fetch(`${API}/api/flowai/safety-switches`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [which]: value }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setSw({ dry_run: d.dry_run !== false, require_double_confirm: d.require_double_confirm !== false });
        setMsg({ type: 'ok', text: 'Gespeichert.' });
        await load();
      } else {
        setMsg({ type: 'err', text: d.error || 'Fehler beim Speichern' });
      }
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setSaving(null);
    }
  }

  if (loading) return <div className="p-6 text-sm">Lade Sicherheits-Schalter…</div>;

  return (
    <div className="p-6 max-w-3xl">
      <FlowAIBreadcrumb trail={[{ label: 'FlowAI', to: '/flowai' }, { label: 'Admin' }, { label: 'Sicherheit' }]} />
      <PageHeader
        title="FlowAI Sicherheits-Schalter"
        subtitle="Globale Schutzmechanismen für den Scharfschalt-Test des Vorschlags-Modus (#1280)."
        actions={<Button variant="ghost" onClick={() => navigate('/flowai')}>← Cockpit</Button>}
      />

      {sw.dry_run && (
        <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-[13px] text-indigo-900">
          🧪 <b>Testmodus aktiv</b> — FlowAI erzeugt Vorschläge und Freigaben werden gespeichert, aber <b>es wird nichts gesendet</b>. Erst nach Abschalten des Dry-Run werden Aktionen scharf ausgeführt.
        </div>
      )}

      <Card title="Dry-Run (Testmodus)">
        <ToggleRow
          label="Dry-Run aktiv — jede Ausführung gesperrt"
          desc="AN: FlowAI plant + Vorschläge können freigegeben werden, aber es wird KEINE Mail / Telegram / Case-Änderung ausgeführt — auch nicht bei bereits freigegebenen oder doppelt bestätigten Aktionen. Dieser Schalter überschreibt alles. Zum Scharfschalten ausschalten."
          checked={sw.dry_run}
          onChange={(v) => toggle('dry_run', v)}
          busy={saving === 'dry_run'}
          tone="indigo"
        />
      </Card>

      <Card title="2. Bestätigung beim Senden">
        <ToggleRow
          label={'„Wirklich senden?“-Dialog vor jeder echten Ausführung'}
          desc="AN: Vor jeder scharfen Ausführung einer freigegebenen Aktion erscheint eine zweite Sicherheitsabfrage. Sobald Vertrauen da ist, kann diese globale Bestätigung abgeschaltet werden (dann wird direkt nach Freigabe ausgeführt). Wirkt nur wenn Dry-Run AUS ist."
          checked={sw.require_double_confirm}
          onChange={(v) => toggle('require_double_confirm', v)}
          busy={saving === 'require_double_confirm'}
          tone="amber"
        />
      </Card>

      {msg && (
        <div className={`mb-4 text-sm ${msg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</div>
      )}

      <Card title="Änderungs-Protokoll">
        {log.length === 0 ? (
          <div className="text-[12px] text-[var(--text-tertiary,#6B7280)]">Noch keine Schalter-Wechsel protokolliert.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[var(--text-tertiary,#6B7280)] border-b border-[var(--border-subtle,#E5E7EB)]">
                <th className="py-1.5 pr-3">Zeitpunkt</th>
                <th className="py-1.5 pr-3">Schalter</th>
                <th className="py-1.5 pr-3">Wechsel</th>
                <th className="py-1.5">Von</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.id} className="border-b border-[var(--border-subtle,#F1F2F4)]">
                  <td className="py-1.5 pr-3">{new Date(e.changed_at).toLocaleString('de-AT')}</td>
                  <td className="py-1.5 pr-3">{e.switch_key === 'flowai_dry_run' ? 'Dry-Run' : '2.-Bestätigung'}</td>
                  <td className="py-1.5 pr-3">{e.old_value ? 'AN' : 'AUS'} → <b>{e.new_value ? 'AN' : 'AUS'}</b></td>
                  <td className="py-1.5">{e.changed_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-4 leading-snug">
        Werte in <code>system_config</code>: <code>flowai_dry_run</code>, <code>flowai_require_double_confirm</code> (Kategorie <code>flowai</code>).
        Der Dry-Run-Guard greift zentral im <code>action-executor</code> — der einzige Ausführungs-Chokepoint.
      </div>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, busy, tone = 'indigo' }) {
  const onCls = tone === 'amber' ? 'bg-amber-500' : 'bg-indigo-500';
  return (
    <div className="py-1.5">
      <label className="flex items-start gap-3 cursor-pointer">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={busy}
          onClick={() => onChange(!checked)}
          className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? onCls : 'bg-[#D1D5DB]'} ${busy ? 'opacity-50' : ''}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
        <span className="flex-1">
          <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{label}</span>
          <span className="block text-[12px] text-[var(--text-tertiary,#6B7280)] mt-0.5 leading-snug">{desc}</span>
        </span>
      </label>
    </div>
  );
}
